import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WorkflowState, WorkflowTransition } from "@app-factory/shared";
import { api } from "@/lib/api";
import { cs } from "@/i18n/cs";
import {
  Btn,
  DetailHeader,
  Field,
  Panel,
  inputClass,
  toneColor,
  type Tone,
} from "@/design/primitives";
import { useAuth } from "@/auth/auth";

/**
 * Workflow Builder (spec §8, /Design): řetěz uzlů s tvary dle druhu
 * (gate ◆, handoff ⬚, návraty ↩), panel vlastností vybraného uzlu, editace.
 */
export function WorkflowBuilderPage() {
  const { key } = useParams({ strict: false }) as { key: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { session } = useAuth();
  const departments = useQuery({
    queryKey: ["departments"],
    queryFn: api.departments,
  });
  const workflows = useQuery({
    queryKey: ["workflows"],
    queryFn: api.workflows,
  });

  const department =
    departments.data?.departments.find((d) => d.key === key) ?? null;
  const workflow =
    workflows.data?.workflows.find((w) => w.key === department?.workflowKey) ??
    null;

  const [states, setStates] = useState<WorkflowState[] | null>(null);
  const [transitions, setTransitions] = useState<WorkflowTransition[] | null>(
    null,
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    if (workflow && states === null) {
      setStates(workflow.states);
      setTransitions(workflow.transitions);
    }
  }, [workflow, states]);

  const saveMutation = useMutation({
    mutationFn: () =>
      api.updateWorkflow(workflow!.key, {
        states: states!,
        transitions: transitions!,
      }),
    onSuccess: () => {
      setDirty(false);
      setSavedAt(Date.now());
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ["workflows"] });
    },
    onError: (err) =>
      setError(err instanceof Error ? err.message : cs.common.error),
  });

  const selected = states?.find((s) => s.key === selectedKey) ?? null;
  const isOwner = session?.user.role === "OWNER";

  const rows = useMemo(() => {
    if (!states || !transitions) return [];
    const ordered = [...states].sort((a, b) => a.order - b.order);
    const forward = ordered.filter(
      (s) => s.kind !== "FAILED" && s.kind !== "BLOCKED",
    );
    const special = ordered.filter(
      (s) => s.kind === "FAILED" || s.kind === "BLOCKED",
    );
    return [forward, special].filter((r) => r.length > 0);
  }, [states, transitions]);

  if (!department || !workflow || !states || !transitions) return null;

  const returns = transitions.filter((t) => t.kind === "RETURN");

  const mutateStates = (next: WorkflowState[]) => {
    setStates(next);
    setDirty(true);
  };
  const mutateTransitions = (next: WorkflowTransition[]) => {
    setTransitions(next);
    setDirty(true);
  };

  const addState = () => {
    const key = prompt("Klíč nového stavu (A–Z, podtržítka):", "NOVY_STAV");
    if (
      !key ||
      !/^[A-Z][A-Z0-9_]{1,30}$/.test(key) ||
      states.some((s) => s.key === key)
    )
      return;
    mutateStates([
      ...states,
      {
        key,
        label: key.replace(/_/g, " "),
        tone: "cyan",
        kind: "WORK",
        order: states.length,
      },
    ]);
    setSelectedKey(key);
  };

  return (
    <div className="af-animate-fade">
      <DetailHeader
        back={
          <Link to="/departments" style={{ color: "inherit" }}>
            {cs.departments.backToDepartments}
          </Link>
        }
        title={cs.departments.builderTitle}
        meta={
          <div className="flex max-w-full gap-1.5 overflow-x-auto whitespace-nowrap md:ml-2">
            {(departments.data?.departments ?? [])
              .filter((d) => d.enabled)
              .slice(0, 8)
              .map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className="flex-none cursor-pointer px-3 py-1 text-[11px] tracking-[0.1em]"
                  style={{
                    borderRadius: "var(--radius-box)",
                    border: `1px solid ${d.key === key ? toneColor[(d.color as Tone) ?? "cyan"] : "var(--border-default)"}`,
                    color:
                      d.key === key
                        ? toneColor[(d.color as Tone) ?? "cyan"]
                        : "var(--text-secondary)",
                  }}
                  onClick={() => {
                    setStates(null);
                    setTransitions(null);
                    setSelectedKey(null);
                    void navigate({
                      to: "/departments/$key",
                      params: { key: d.key },
                    });
                  }}
                >
                  {d.name.toUpperCase()}
                </button>
              ))}
          </div>
        }
        right={
          <>
            {savedAt ? (
              <span
                className="text-[11px]"
                style={{ color: "var(--positive)" }}
              >
                ✓ uloženo
              </span>
            ) : null}
            {isOwner ? (
              <>
                <Btn onClick={addState}>{cs.departments.addState}</Btn>
                <Btn
                  variant="primary"
                  disabled={!dirty || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                  data-testid="workflow-save"
                >
                  {cs.departments.save}
                </Btn>
              </>
            ) : null}
          </>
        }
      />

      {error ? (
        <div className="mb-2 text-[12px]" style={{ color: "var(--critical)" }}>
          ⚠ {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_320px]">
        <Panel className="overflow-auto p-5">
          <div className="af-label-dim mb-4">
            {department.name.toUpperCase()} · workflow{" "}
            {workflow.name.toUpperCase()}
          </div>
          {rows.map((row, ri) => (
            <div key={ri} className="mb-4 flex flex-wrap items-center">
              {row.map((state, i) => {
                const isGate = state.kind === "GATE";
                const isDone = state.kind === "DONE";
                const isBlockedOrFailed =
                  state.kind === "BLOCKED" || state.kind === "FAILED";
                const color = toneColor[(state.tone as Tone) ?? "cyan"];
                const active = selectedKey === state.key;
                return (
                  <div key={state.key} className="flex items-center">
                    <button
                      type="button"
                      onClick={() => setSelectedKey(state.key)}
                      className="min-w-[96px] cursor-pointer px-3.5 py-2.5 text-center"
                      style={{
                        border: `1px ${isBlockedOrFailed ? "dashed" : "solid"} ${active ? color : `${color}66`}`,
                        background: active ? `${color}14` : "transparent",
                        transform: isGate ? "skewX(-8deg)" : undefined,
                        boxShadow: isDone ? `0 0 12px ${color}33` : undefined,
                      }}
                      data-testid={`wf-node-${state.key}`}
                    >
                      <div
                        className="text-[11px] tracking-[0.08em]"
                        style={{
                          color,
                          transform: isGate ? "skewX(8deg)" : undefined,
                        }}
                      >
                        {isGate ? "◆ " : ""}
                        {state.label}
                      </div>
                      {state.note ? (
                        <div
                          className="mt-0.5 text-[11px]"
                          style={{
                            color: "var(--text-tertiary)",
                            transform: isGate ? "skewX(8deg)" : undefined,
                          }}
                        >
                          {state.note}
                        </div>
                      ) : null}
                    </button>
                    {i < row.length - 1 ? (
                      <span
                        className="px-2 text-[12px]"
                        style={{ color: "var(--accent)" }}
                      >
                        →
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}

          {returns.length > 0 ? (
            <div
              className="mt-1 text-[11px]"
              style={{ color: "var(--text-secondary)" }}
            >
              {returns.map((t) => (
                <div key={`${t.from}-${t.to}`}>
                  ↩ {t.from} → {t.to}
                  {t.label ? ` · ${t.label}` : ""}
                </div>
              ))}
            </div>
          ) : null}

          <div
            className="mt-3 flex flex-wrap gap-x-4 gap-y-1 pt-3 text-[11px]"
            style={{
              borderTop:
                "1px solid color-mix(in srgb, var(--accent) 6%, transparent)",
              color: "var(--text-tertiary)",
            }}
          >
            <span>
              <span style={{ color: "var(--accent)" }}>■</span>{" "}
              {cs.departments.legend.state}
            </span>
            <span>
              <span style={{ color: "var(--warning)" }}>◆</span>{" "}
              {cs.departments.legend.gate}
            </span>
            <span>
              <span style={{ color: "var(--info)" }}>⬚</span>{" "}
              {cs.departments.legend.handoff}
            </span>
            <span>
              <span style={{ color: "var(--text-secondary)" }}>↩</span>{" "}
              {cs.departments.legend.ret}
            </span>
          </div>
        </Panel>

        <Panel className="self-start p-4">
          <div className="af-label mb-3" style={{ color: "var(--accent)" }}>
            {cs.departments.selectedNode} // {selected?.label ?? "—"}
          </div>
          {selected ? (
            <NodeEditor
              state={selected}
              states={states}
              transitions={transitions}
              editable={isOwner}
              onChangeState={(next) =>
                mutateStates(
                  states.map((s) => (s.key === selected.key ? next : s)),
                )
              }
              onDelete={() => {
                mutateStates(states.filter((s) => s.key !== selected.key));
                mutateTransitions(
                  transitions.filter(
                    (t) => t.from !== selected.key && t.to !== selected.key,
                  ),
                );
                setSelectedKey(null);
              }}
              onAddTransition={(to, kind) =>
                mutateTransitions([
                  ...transitions,
                  { from: selected.key, to, kind },
                ])
              }
              onRemoveTransition={(to) =>
                mutateTransitions(
                  transitions.filter(
                    (t) => !(t.from === selected.key && t.to === to),
                  ),
                )
              }
            />
          ) : (
            <div
              className="text-[12px]"
              style={{ color: "var(--text-tertiary)" }}
            >
              Vyber uzel kliknutím.
            </div>
          )}
          <div
            className="mt-3 text-[11px] leading-relaxed"
            style={{ color: "var(--text-tertiary)" }}
          >
            {cs.departments.builderNote}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function NodeEditor({
  state,
  states,
  transitions,
  editable,
  onChangeState,
  onDelete,
  onAddTransition,
  onRemoveTransition,
}: {
  state: WorkflowState;
  states: WorkflowState[];
  transitions: WorkflowTransition[];
  editable: boolean;
  onChangeState: (next: WorkflowState) => void;
  onDelete: () => void;
  onAddTransition: (to: string, kind: "NORMAL" | "RETURN") => void;
  onRemoveTransition: (to: string) => void;
}) {
  const outgoing = transitions.filter((t) => t.from === state.key);
  const [newTarget, setNewTarget] = useState("");
  const candidates = states.filter(
    (s) => s.key !== state.key && !outgoing.some((t) => t.to === s.key),
  );

  return (
    <div className="flex flex-col gap-2.5">
      <Field label={cs.fields.label}>
        <input
          className={inputClass}
          value={state.label}
          disabled={!editable}
          onChange={(e) => onChangeState({ ...state, label: e.target.value })}
        />
      </Field>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Field label={cs.fields.kind}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={state.kind}
            disabled={!editable}
            onChange={(e) =>
              onChangeState({
                ...state,
                kind: e.target.value as WorkflowState["kind"],
              })
            }
          >
            {(
              ["START", "WORK", "GATE", "BLOCKED", "DONE", "FAILED"] as const
            ).map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
        </Field>
        <Field label={cs.fields.color}>
          <select
            className={inputClass}
            style={{ background: "var(--surface-inset)" }}
            value={state.tone}
            disabled={!editable}
            onChange={(e) =>
              onChangeState({
                ...state,
                tone: e.target.value as WorkflowState["tone"],
              })
            }
          >
            {(["cyan", "green", "amber", "red", "violet", "dim"] as const).map(
              (t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ),
            )}
          </select>
        </Field>
      </div>
      <Field label={cs.fields.nodeNote}>
        <input
          className={inputClass}
          value={state.note ?? ""}
          disabled={!editable}
          onChange={(e) =>
            onChangeState({ ...state, note: e.target.value || undefined })
          }
        />
      </Field>

      <div className="af-label-dim mt-1">TRANSITIONS Z TOHOTO STAVU</div>
      {outgoing.map((t) => (
        <div
          key={t.to}
          className="flex items-center justify-between text-[11px]"
        >
          <span style={{ color: "var(--text-secondary)" }}>
            → {t.to}
            {t.kind === "RETURN" ? " ↩" : ""}
            {t.label ? ` · ${t.label}` : ""}
          </span>
          {editable ? (
            <button
              type="button"
              className="cursor-pointer text-[11px]"
              style={{ color: "var(--critical)" }}
              onClick={() => onRemoveTransition(t.to)}
            >
              ✕
            </button>
          ) : null}
        </div>
      ))}
      {editable && candidates.length > 0 ? (
        <div className="flex items-end gap-2">
          <select
            className={`${inputClass} min-w-0`}
            style={{ background: "var(--surface-inset)" }}
            value={newTarget}
            onChange={(e) => setNewTarget(e.target.value)}
          >
            <option value="">přidat →</option>
            {candidates.map((s) => (
              <option key={s.key} value={s.key}>
                {s.key}
              </option>
            ))}
          </select>
          <Btn
            disabled={!newTarget}
            onClick={() => {
              onAddTransition(newTarget, "NORMAL");
              setNewTarget("");
            }}
          >
            +
          </Btn>
        </div>
      ) : null}

      {editable ? (
        <Btn variant="danger" className="mt-1" onClick={onDelete}>
          SMAZAT STAV
        </Btn>
      ) : null}
    </div>
  );
}
